import { formatApiErrorDetail } from "@/lib/api";
import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Star, CheckCircle, Heart, GoogleLogo, YoutubeLogo, FacebookLogo } from "@phosphor-icons/react";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function TenantReview() {
  const { token } = useParams();
  const [ctx, setCtx] = useState(null);
  const [err, setErr] = useState(null);
  const [stars, setStars] = useState(0);
  const [hover, setHover] = useState(0);
  const [comment, setComment] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { is_public, review_urls, stars }

  useEffect(() => {
    axios.get(`${BASE}/api/public/review/${token}`)
      .then(r => setCtx(r.data))
      .catch(e => setErr(e.response?.data?.detail || "Review link not found"));
  }, [token]);

  const submit = async () => {
    if (!stars) { toast.error("Pick a star rating first"); return; }
    setSubmitting(true);
    try {
      const r = await axios.post(`${BASE}/api/public/review/${token}`, { stars, comment });
      setResult({ ...r.data, stars });
      if (r.data.is_public && comment.trim()) {
        try { await navigator.clipboard.writeText(comment); } catch {}
      }
    } catch (e) {
      toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Could not submit — try again"));
    } finally { setSubmitting(false); }
  };

  if (err) return (
    <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center p-6">
      <div className="max-w-md text-center">
        <div className="text-4xl mb-3">🔗</div>
        <h1 className="font-black text-2xl mb-2">Review link expired</h1>
        <p className="text-slate-400">{err}</p>
      </div>
    </div>
  );
  if (!ctx) return <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center">Loading…</div>;

  const brand = ctx.brand_color || "#00C4CC";

  if (ctx.already_submitted && !result) return <ThankYou stars={ctx.stars} brand={brand} businessName={ctx.business_name}/>;
  if (result) {
    return result.is_public
      ? <PublicPrompt result={result} ctx={ctx} comment={comment} brand={brand}/>
      : <PrivateThanks brand={brand} businessName={ctx.business_name}/>;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 text-white p-6 flex flex-col items-center justify-center">
      <div className="w-full max-w-lg" data-testid="tenant-review-page">
        {ctx.logo_url && <img src={ctx.logo_url} alt="" className="h-14 mx-auto mb-6"/>}
        <div className="text-center mb-8">
          <h1 className="font-display font-black text-3xl sm:text-4xl uppercase tracking-tight">How'd we do?</h1>
          <p className="mt-2 text-slate-300 text-sm">
            {ctx.tenant_name ? `Hi ${ctx.tenant_name} — ` : ""}
            {ctx.business_name} just wrapped work at{" "}
            <b>{ctx.property_address}</b>. Tap a star.
          </p>
        </div>

        <div className="flex justify-center gap-2 mb-6" data-testid="tenant-review-stars">
          {[1, 2, 3, 4, 5].map(n => {
            const active = n <= (hover || stars);
            return (
              <button
                key={n}
                type="button"
                onMouseEnter={() => setHover(n)}
                onMouseLeave={() => setHover(0)}
                onClick={() => setStars(n)}
                data-testid={`tenant-review-star-${n}`}
                aria-label={`${n} star${n > 1 ? "s" : ""}`}
                className="p-2 focus:outline-none transition-transform hover:scale-110"
              >
                <Star weight={active ? "fill" : "regular"} size={44}
                  className={active ? "text-amber-400" : "text-slate-500"}/>
              </button>
            );
          })}
        </div>

        {stars > 0 && (
          <>
            <textarea
              value={comment}
              onChange={e => setComment(e.target.value)}
              rows={4}
              placeholder={stars >= 4 ? "What did we do well? (optional — we'll copy this to your clipboard so you can paste into Google)" : "What went wrong? We'll fix it — this feedback stays private."}
              data-testid="tenant-review-comment"
              className="w-full px-4 py-3 bg-slate-800 border-2 border-slate-700 text-white placeholder-slate-500 focus:border-white focus:outline-none rounded-none"
            />
            <button
              onClick={submit}
              disabled={submitting}
              data-testid="tenant-review-submit"
              style={{ backgroundColor: brand }}
              className="mt-4 w-full h-14 text-white font-black uppercase tracking-widest text-sm disabled:opacity-40 hover:opacity-90"
            >
              {submitting ? "Sending…" : `Submit ${stars} Star${stars > 1 ? "s" : ""}`}
            </button>
          </>
        )}
      </div>
    </div>
  );
}

function ThankYou({ stars, brand, businessName }) {
  return (
    <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center p-6">
      <div className="text-center max-w-md" data-testid="tenant-review-already-submitted">
        <CheckCircle weight="fill" size={64} className="mx-auto mb-4" style={{ color: brand }}/>
        <h1 className="font-display font-black text-3xl uppercase mb-2">You already reviewed us</h1>
        <p className="text-slate-400">You gave {businessName} <b>{stars} of 5 stars</b>. Thanks!</p>
      </div>
    </div>
  );
}

function PrivateThanks({ brand, businessName }) {
  return (
    <div className="min-h-screen bg-slate-900 text-white flex items-center justify-center p-6">
      <div className="text-center max-w-md" data-testid="tenant-review-private-thanks">
        <Heart weight="fill" size={64} className="mx-auto mb-4 text-rose-400"/>
        <h1 className="font-display font-black text-3xl uppercase mb-2">Thanks for the honest take</h1>
        <p className="text-slate-400">
          {businessName} got your feedback directly. Someone will reach out to make it right.
        </p>
      </div>
    </div>
  );
}

function PublicPrompt({ result, ctx, comment, brand }) {
  const urls = result.review_urls || {};
  const has = Object.values(urls).some(v => v);
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 to-slate-800 text-white flex items-center justify-center p-6">
      <div className="max-w-lg w-full text-center" data-testid="tenant-review-public-prompt">
        <div className="text-6xl mb-3">🎉</div>
        <h1 className="font-display font-black text-3xl sm:text-4xl uppercase mb-3">You made our day!</h1>
        <p className="text-slate-300 mb-6">
          {result.stars}-star reviews on Google mean the world to a small team.
          {comment && " We've copied your comment to your clipboard — paste it in and post."}
        </p>

        {has ? (
          <div className="space-y-3" data-testid="tenant-review-public-links">
            {urls.google && (
              <a href={urls.google} target="_blank" rel="noreferrer"
                data-testid="tenant-review-google-cta"
                style={{ backgroundColor: brand }}
                className="w-full h-14 text-white font-black uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2 hover:opacity-90">
                <GoogleLogo size={18} weight="bold"/> Post on Google
              </a>
            )}
            {urls.yelp && (
              <a href={urls.yelp} target="_blank" rel="noreferrer"
                data-testid="tenant-review-yelp-cta"
                className="w-full h-14 bg-red-600 text-white font-black uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2 hover:opacity-90">
                Post on Yelp
              </a>
            )}
            {urls.facebook && (
              <a href={urls.facebook} target="_blank" rel="noreferrer"
                data-testid="tenant-review-facebook-cta"
                className="w-full h-14 bg-blue-600 text-white font-black uppercase tracking-widest text-sm inline-flex items-center justify-center gap-2 hover:opacity-90">
                <FacebookLogo size={18} weight="bold"/> Post on Facebook
              </a>
            )}
          </div>
        ) : (
          <div className="text-slate-400 text-sm p-4 bg-slate-800/60 border border-slate-700" data-testid="tenant-review-no-urls">
            Thanks so much! (The business hasn't hooked up their public review pages yet — but your rating was saved.)
          </div>
        )}
      </div>
    </div>
  );
}
